import CreditsCharge from '@components/CreditsCharge';
import {
  Copy, MoreHorizontal, Pencil
} from '@huspy/briks-icons';
import {
  Flex, IconButton, Menu,
  Text
} from '@huspy/briks-web';
import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { CREDITS_CHARGE_ACTION_TYPES } from '@modules/core/api/const';
import useCredits from '@modules/core/hooks/useCredits';
import useDuplicateProposal from '@modules/proposals/api/mutation/useDuplicateProposal';
import { ProposalDomain } from '@modules/proposals/domain/proposal';
import { ProposalCreationClientAndMortgageRoute } from '@modules/proposals/presentation/routes';
import { PROPOSAL_CREATION_RULES } from '@modules/proposals/presentation/v2/pages/ProposalCreation/rules';
import Loader from '@shared/loader';
import { useNavigate } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';

type MenuItem = {
  text: string;
  isPending?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  hide?: boolean;
  rightIcon?: React.ReactNode;
  onClick?: Function;
};

type ItemsMenuProps = {
  items: MenuItem[];
};

const onMenuClick = (e: { stopPropagation: () => void; }) => {
  e.stopPropagation();
};

export const ItemsMenu = ({ items }: ItemsMenuProps) => (
  <Menu.Root>
    <Menu.Trigger asChild>
      <IconButton variant='tertiary' size='sm' onClick={ onMenuClick } data-test='proposal-items-menu'>
        <MoreHorizontal />
      </IconButton>
    </Menu.Trigger>
    <Menu.Positioner>
      <Menu.Content>
        <Menu.ItemGroup>
          {items.map(({
            text, hide, isPending, disabled, onClick, icon, rightIcon,
          }) =>
            !hide && (
              <Menu.Item
                key={ text }
                disabled={ isPending || disabled }
                value={ text }
                onClick={ (e) => {
                  e.stopPropagation();
                  if (!isPending && !disabled && onClick) onClick();
                } }
              >
                <Flex
                  gap={ 2 }
                  align='center'
                  opacity={ isPending || disabled ? 0.4 : 1 }
                >
                  {isPending && <Loader datatestId={ `menu-item-loader-${text}` } />}
                  {!isPending && icon}
                  {text}
                  {rightIcon}
                </Flex>
              </Menu.Item>
            ))}
        </Menu.ItemGroup>
      </Menu.Content>
    </Menu.Positioner>
  </Menu.Root>
);

type Props = {
  proposal: ProposalEntity;
};

const ProposalItemsMenu = ({ proposal }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const proposalExternalId = proposal.external_id;
  const canEdit = ProposalDomain.canEdit(proposal);
  const canDuplicate = ProposalDomain.canDuplicate(proposal);

  const { mutateAsync, isPending: isDuplicatingPending } = useDuplicateProposal();

  const { callCreditsAction } = useCredits();

  const onClickDuplicateProposal = async () => {
    callCreditsAction(
      CREDITS_CHARGE_ACTION_TYPES.proposalGenerate,
      async () => {
        const newProposal = await mutateAsync({ external_id: proposalExternalId });

        navigate({
          to: ProposalCreationClientAndMortgageRoute.to,
          params: { id: newProposal.external_id },
        });
      },
      (error: Error) => {
        trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_DUPLICATION_FAILED, {
          useContext: true,
          blocked: 1,
          proposal_id: proposalExternalId,
          error_message: error.message,
        });
      }
    );
  };

  const onClickEditProposal = () => {
    navigate({
      to: PROPOSAL_CREATION_RULES.getCreateProposalLatestRoute(proposal),
      params: { id: proposalExternalId },
    });
  };

  return (
    <ItemsMenu
      items={ [
        {
          text: t('proposals.table.duplicateProposal'),
          isPending: isDuplicatingPending,
          icon: <Copy />,
          rightIcon: (
            <Flex gap={ 2 }>
              <Text color='text-placeholder_subtle'>|</Text>
              <CreditsCharge
                action_type={ CREDITS_CHARGE_ACTION_TYPES.proposalGenerate }
                showLeftLine={ false }
              />
            </Flex>
          ),
          hide: !canDuplicate,
          onClick: onClickDuplicateProposal,
        },
        {
          disabled: !canEdit,
          text: canEdit ? t('proposals.table.editProposal') : t('proposals.table.editProposalUnavailable'),
          icon: <Pencil />,
          onClick: onClickEditProposal,
        }
      ] }
    />
  );
};

export default ProposalItemsMenu;
